import get from 'lodash/get';

import { mergeTrackingProperties, transformNamedAnswers } from './calendarCommon';
import { CAL_URL_REGEX, NAMED_ANSWERS } from './constants';

import type { FormValues, Input } from '$lib/types/Form';

let calScriptsEnqueued = false;
let calInitializedDomain: 'com' | 'eu' | null = null;

// Cal.com built-in field labels that can be prefilled
// These are the field names Cal.com's API expects
const CAL_BUILT_IN_FIELDS = {
    name: 'name',
    email: 'email',
    phone: 'attendeePhoneNumber',
    notes: 'notes',
    company: 'company',
} as const;

interface CalConfig {
    calLink: string;
    config: {
        name?: string;
        email?: string;
        phone?: string;
        notes?: string;
        utm_source?: string;
        utm_medium?: string;
        utm_campaign?: string;
        utm_content?: string;
        utm_term?: string;
        [key: string]: unknown;
    };
}

/**
 * Cal.com integration module
 * Provides utilities for loading scripts, validating URLs, extracting form data,
 * and building configuration for Cal.com popup widgets
 */
export const cal = {
    /**
     * Loads Cal.com embed scripts
     * Supports both .com and .eu domains
     * Supports organization subdomains (e.g., test.cal.com)
     * Supports custom domains — embed is loaded from app.cal.com but origin is set to the custom domain
     * Only loads once per page session
     * @param domain - The Cal.com domain to use ('com' or 'eu')
     * @param orgSubdomain - Optional organization subdomain (e.g., 'test' for test.cal.com)
     * @param customDomain - Optional custom domain hostname (e.g., 'booking.mycompany.com')
     */
    enqueueScripts: (
        domain: 'com' | 'eu' = 'com',
        orgSubdomain: string | null = null,
        customDomain: string | null = null,
    ) => {
        // If already initialized with a different domain, warn but continue
        if (calScriptsEnqueued && calInitializedDomain !== domain) {
            console.warn(
                `Cal.com already initialized with .${calInitializedDomain} domain. Cannot switch to .${domain}`,
            );
            return;
        }

        if (calScriptsEnqueued) {
            return;
        }

        // For organization subdomains, load from the org's subdomain; otherwise use app.cal.(com/eu)
        const embedUrl = orgSubdomain
            ? `https://${orgSubdomain}.cal.${domain}/embed/embed.js`
            : `https://app.cal.${domain}/embed/embed.js`;

        // For custom domains, set origin to the custom domain so Cal.com's embed communicates correctly
        const origin = customDomain
            ? `https://${customDomain}`
            : orgSubdomain
            ? `https://${orgSubdomain}.cal.${domain}`
            : `https://cal.${domain}`;

        // Cal embed initialization
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (function (C: any, A: string, L: string) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const p = function (a: any, ar: any) {
                a.q.push(ar);
            };
            const d = C.document;
            C.Cal =
                C.Cal ||
                function () {
                    const cal = C.Cal;
                    // eslint-disable-next-line prefer-rest-params
                    const ar = arguments;

                    if (!cal.loaded) {
                        cal.ns = {};
                        cal.q = cal.q || [];
                        d.head.appendChild(d.createElement('script')).src = A;
                        cal.loaded = true;
                    }

                    if (ar[0] === L) {
                        const api = function () {
                            // eslint-disable-next-line prefer-rest-params
                            p(api, arguments);
                        };
                        const namespace = ar[1];
                        // @ts-expect-error - q is not defined in the type definition
                        api.q = api.q || [];

                        if (typeof namespace === 'string') {
                            cal.ns[namespace] = cal.ns[namespace] || api;
                            p(cal.ns[namespace], ar);
                            p(cal, ['initNamespace', namespace]);
                        } else p(cal, ar);
                        return;
                    }
                    p(cal, ar);
                };
        })(window, embedUrl, 'init');

        // Initialize Cal.com with the appropriate origin
        window.Cal('init', { origin });

        window.Cal('ui', {
            hideEventTypeDetails: false,
            layout: 'month_view',
        });

        calScriptsEnqueued = true;
        calInitializedDomain = domain;
    },

    /**
     * Workaround as Cal does not provide a way to close the modal programatically
     */
    closeModal: () => {
        const escEvent = new KeyboardEvent('keydown', {
            key: 'Escape',
            code: 'Escape',
            keyCode: 27,
            bubbles: true,
        });
        document.dispatchEvent(escEvent);
    },

    /**
     * Validates if a URL matches the Cal.com URL format
     */
    validateUrl: (url: string): boolean => {
        return CAL_URL_REGEX.test(url);
    },

    /**
     * Detects whether the Cal.com URL is .eu or .com
     * Custom domains always default to 'com'
     */
    getCalDomain: (url: string): 'eu' | 'com' => {
        return url.includes('cal.eu') ? 'eu' : 'com';
    },

    /**
     * Extracts the organization subdomain from a Cal.com URL
     * Returns null for standard cal.com/cal.eu URLs and custom domain URLs
     * Returns the subdomain for organization URLs (e.g., 'lc' from 'https://lc.cal.com/...')
     */
    getOrgSubdomain: (url: string): string | null => {
        const match = url.match(/https:\/\/([a-z0-9-]+)\.cal\.(com|eu)\//);

        if (match && match[1] !== 'app') {
            return match[1];
        }

        return null;
    },

    /**
     * Extracts the hostname from a custom domain Cal.com URL
     * Returns null for standard cal.com/cal.eu URLs
     */
    getCustomDomain: (url: string): string | null => {
        if (cal.validateUrl(url)) return null;

        try {
            return new URL(url).hostname;
        } catch {
            return null;
        }
    },

    /**
     * Extracts the cal link from a full Cal.com URL
     * Example: https://cal.com/namespace/event-name -> namespace/event-name
     * Example: https://cal.eu/namespace/event-name -> namespace/event-name
     * Example: https://test.cal.com/media/event-name -> media/event-name (organization subdomain)
     * Example: https://booking.mycompany.com/namespace/event-name -> namespace/event-name (custom domain)
     */
    extractCalLink: (url: string): string => {
        const withoutStandardDomain = url.replace(/https:\/\/([a-z0-9-]+\.)?cal\.(com|eu)\//, '');

        if (withoutStandardDomain !== url) {
            return withoutStandardDomain;
        }

        // Custom domain: extract path without leading slash
        try {
            return new URL(url).pathname.replace(/^\//, '');
        } catch {
            return url;
        }
    },

    /**
     * Transforms name field variations (fname/lname) to standard firstName/lastName format
     */
    transformNamedAnswers: (namedAnswers: FormValues): FormValues => {
        return transformNamedAnswers(namedAnswers);
    },

    /**
     * Extracts form answers for Cal.com API
     * - Named answers (email, name, phone) are extracted from form inputs
     * - Tracking properties from previous pages are merged (but not overwritten)
     * - Cal.com only supports prefilling built-in fields (name, email, phone, notes)
     */
    extractNamedAnswers: (
        inputs: Input[],
        formData: FormValues,
        allTrackingProperties: FormValues,
    ): FormValues => {
        let counter = 1;
        const namedAnswers: FormValues = {};

        let messageFilled = false;

        // Extract named answers from current form inputs
        inputs.forEach((input) => {
            const { name } = input;
            const value = get(formData, name, '');

            // Check if field name matches our known fields
            if (NAMED_ANSWERS.includes(name)) {
                namedAnswers[name] = value;
            } else if ((name === 'notes' || name.startsWith('message-')) && !messageFilled) {
                namedAnswers.message = value;
                messageFilled = true;
            }

            counter += 1;
        });

        const mergedNamedAnswers = mergeTrackingProperties({
            allTrackingProperties,
            namedAnswers,
            maxTrackingProperties: counter,
        });

        return cal.transformNamedAnswers(mergedNamedAnswers);
    },

    /**
     * Builds the configuration object for Cal.com popup widget
     * Maps our internal field names to Cal.com's built-in field labels
     * Includes UTM parameters in the config alongside prefill data
     */
    buildConfig: (
        calUrl: string,
        namedAnswers: FormValues,
        trackingProperties: FormValues,
    ): CalConfig => {
        const calLink = cal.extractCalLink(calUrl);
        const {
            name: directName,
            firstName,
            lastName,
            email,
            phone,
            message,
            company,
        } = namedAnswers;

        // Determine name value with priority:
        // 1. Use 'name' if provided directly
        // 2. Combine firstName + lastName if both exist
        // 3. Use firstName or lastName if only one exists
        let finalName = '';

        if (directName) {
            finalName = directName as string;
        } else if (firstName && lastName) {
            finalName = `${firstName} ${lastName}`;
        } else if (firstName) {
            finalName = firstName as string;
        } else if (lastName) {
            finalName = lastName as string;
        }

        const { utm_campaign, utm_source, utm_medium, utm_content, utm_term } = trackingProperties;

        return {
            calLink,
            config: {
                ...(finalName && { [CAL_BUILT_IN_FIELDS.name]: finalName }),
                ...(email && { [CAL_BUILT_IN_FIELDS.email]: email as string }),
                ...(phone && { [CAL_BUILT_IN_FIELDS.phone]: phone as string }),
                ...(message && { [CAL_BUILT_IN_FIELDS.notes]: message as string }),
                ...(company && { [CAL_BUILT_IN_FIELDS.company]: company as string }),
                // Add UTM parameters to config
                ...(utm_campaign && { utm_campaign: utm_campaign as string }),
                ...(utm_source && { utm_source: utm_source as string }),
                ...(utm_medium && { utm_medium: utm_medium as string }),
                ...(utm_content && { utm_content: utm_content as string }),
                ...(utm_term && { utm_term: utm_term as string }),
            },
        };
    },
};
